import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { useState } from 'react'
import {
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
	data,
	Form,
	Link,
	useActionData,
	useSearchParams,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { Divider } from '#app/components/divider.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import { ProviderConnectionForm, providerNames } from '#app/utils/connections.tsx'
import { validateCSRF } from '#app/utils/csrf.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { PasswordSchema, UsernameOrEmailSchema } from '#app/utils/user-validation.ts'
import { handleNewSession } from './utils/functions.server'

const LoginFormSchema = z.object({
	username: UsernameOrEmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return data({})
}

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request)
	let formData: FormData | null = null
	try {
		formData = await request.formData()
	} catch (error) {
		throw new Response('Invalid payload', { status: 400 })
	}
	await validateCSRF(formData, request.headers)
	await checkHoneypot(formData)
	const submission = await parse(formData, {
		schema: intent =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== 'submit') return { ...data, session: null }

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Nombre de usuario, correo electrónico o contraseña inválidos',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})
	// get the password off the payload that's sent back
	delete submission.payload.password

	if (submission.intent !== 'submit') {
		// @ts-expect-error - conform should probably have support for doing this
		delete submission.value?.password
		return { status: 'idle', submission } as const
	}
	if (!submission.value?.session) {
		return data({ status: 'error', submission } as const, { status: 400 })
	}

	let { session, remember, redirectTo = '/ai' } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const [showPassword, setShowPassword] = useState(false)

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getFieldsetConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			return parse(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="relative w-full max-w-sm tracking-tight">
			<div className="flex flex-col gap-3 text-center">
				<h1 className="font-regular text-h4 font-medium text-neutral-800 md:text-h2">Bienvenido!</h1>
				<p className="text-md font-light text-muted-foreground">Por favor, ingresa tus credenciales</p>
			</div>
			<Spacer size="xs" />
			<div>
				<div className="mx-auto w-full max-w-lg">
					<Form method="POST" {...form.props}>
						<HoneypotInputs />
						<Field
							labelProps={{ children: 'Nombre de usuario o email' }}
							inputProps={{
								...conform.input(fields.username),
								autoFocus: true,
								placeholder: 'manuel o correo@gmail.com',
								className: 'lowercase py-3 h-auto',
								icon: <Icon name="user" className="h-4 w-4" />,
							}}
							errors={fields.username.errors}
						/>

						<Field
							labelProps={{ children: 'Contraseña' }}
							inputProps={{
								...conform.input(fields.password, {
									type: showPassword ? 'text' : 'password',
								}),
								placeholder: '···········',
								className: 'placeholder:font-bold py-3 h-auto',
								icon: <Icon name="lock-closed" className="h-4 w-4" />,
								// @ts-expect-error - componentAfter is a custom prop
								componentAfter: (
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/30 transition-colors hover:text-foreground/60"
										aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
									>
										<Icon name={showPassword ? 'eye-striken' : 'eye'} className="h-4 w-4" />
									</button>
								),
							}}
							errors={fields.password.errors}
						/>

						<div className="flex flex-col justify-between md:flex-row">
							<CheckboxField
								labelProps={{
									htmlFor: fields.remember.id,
									children: 'Recuérdame',
								}}
								buttonProps={conform.input(fields.remember, {
									type: 'checkbox',
								})}
								errors={fields.remember.errors}
							/>
							<div>
								<Link to="/forgot-password" className="text-body-xs text-neutral-600 underline">
									Olvidé mi contraseña
								</Link>
							</div>
						</div>

						<input {...conform.input(fields.redirectTo, { type: 'hidden' })} />
						<ErrorList errors={form.errors} id={form.errorId} />

						<div className="flex items-center justify-between gap-6 pt-3">
							<StatusButton
								className="h-12 w-full rounded-xl bg-neutral-900 hover:hover:bg-neutral-600 px-4 py-3"
								status={isPending ? 'pending' : (actionData?.status ?? 'idle')}
								type="submit"
								disabled={isPending}
							>
								Iniciar sesión
								<Icon name="arrow-right" className="ml-1 h-4 w-4" />
							</StatusButton>
						</div>
					</Form>
					<Divider className="my-8" text="O continuar con" />
					<ul className="flex flex-col gap-5 ">
						{providerNames.map(providerName => (
							<li key={providerName}>
								<ProviderConnectionForm type="Login" providerName={providerName} redirectTo={redirectTo} />
							</li>
						))}
					</ul>

					<div className="pt-6">
						<span className="flex flex-row items-center justify-center gap-3">
							<span className="text-sm text-muted-foreground/60">Eres nuevo?</span>{' '}
							<Button asChild variant="outline" className="h-auto rounded-full px-3 py-2 text-xs">
								<Link to={redirectTo ? `/signup?redirectTo=${encodeURIComponent(redirectTo)}` : '/signup'}>
									Crea una cuenta
								</Link>
							</Button>
						</span>
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Login to IusPro' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
